import { CSSProperties, forwardRef, HTMLAttributes, ReactNode } from 'react';

import OverflowFadeScrollArea, { OverflowFadeSurface } from '../OverflowFadeScrollArea';

export const FULL_HEIGHT_FLEX_COLUMN_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
};

export const FLEX_CONTENT_REGION_STYLE: CSSProperties = {
    flex: '1 1 0%',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
};

export interface FullHeightFlexColumnProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    children: ReactNode;
}

export interface FlexContentRegionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    children: ReactNode;
    fadeSize?: CSSProperties['height'];
    fadeSurface?: OverflowFadeSurface;
    overflow?: CSSProperties['overflow'];
    overflowFade?: boolean;
    overflowX?: CSSProperties['overflowX'];
    overflowY?: CSSProperties['overflowY'];
}

const FullHeightFlexColumn = forwardRef<HTMLDivElement, FullHeightFlexColumnProps>(
    ({ children, className, style, ...props }, ref): JSX.Element => (
        <div ref={ref} className={className} style={{ ...FULL_HEIGHT_FLEX_COLUMN_STYLE, ...style }} {...props}>
            {children}
        </div>
    ),
);

FullHeightFlexColumn.displayName = 'FullHeightFlexColumn';

const FlexContentRegion = forwardRef<HTMLDivElement, FlexContentRegionProps>(
    (
        {
            children,
            className,
            fadeSize,
            fadeSurface = 'container',
            overflow = 'hidden',
            overflowFade = false,
            overflowX,
            overflowY,
            style,
            ...props
        },
        ref,
    ): JSX.Element => {
        if (overflowFade) {
            return (
                <OverflowFadeScrollArea
                    {...props}
                    className={className}
                    containerStyle={{ ...FLEX_CONTENT_REGION_STYLE, ...style }}
                    fadeSize={fadeSize}
                    ref={ref}
                    style={{ overflow, overflowX, overflowY }}
                    surface={fadeSurface}
                >
                    {children}
                </OverflowFadeScrollArea>
            );
        }

        return (
            <div
                ref={ref}
                className={className}
                style={{
                    ...FLEX_CONTENT_REGION_STYLE,
                    overflow,
                    overflowX,
                    overflowY,
                    ...style,
                }}
                {...props}
            >
                {children}
            </div>
        );
    },
);

FlexContentRegion.displayName = 'FlexContentRegion';

export { FlexContentRegion, FullHeightFlexColumn };
